import { Button, type ButtonName } from "@arena/shared";

/** Joystick radius in CSS pixels. */
const STICK_RADIUS = 56;
/**
 * How many fixed steps a press stays "live". A jump pressed a few frames
 * before landing still fires on landing, and a dash pressed just before the
 * cooldown ends still happens — the simulation needs no extra state for it.
 */
const PRESS_BUFFER_STEPS: Record<ButtonName, number> = {
  Jump: 4,
  Dash: 3,
  Light: 1,
  Grab: 1,
  Special1: 1,
  Special2: 1,
  Slam: 1,
};
const BUTTON_NAMES = Object.keys(Button) as ButtonName[];

const KEY_BUTTONS: Record<string, ButtonName> = {
  Space: "Jump",
  ShiftLeft: "Dash",
  ShiftRight: "Dash",
  KeyJ: "Light",
  KeyK: "Grab",
  KeyU: "Special1",
  KeyI: "Special2",
  KeyL: "Slam",
};

export interface StepSample {
  /** Screen-space stick: x right, y up, magnitude ≤ 1. */
  sx: number;
  sy: number;
  buttons: number;
}

/**
 * Merges multi-touch controls (floating joystick, action buttons, drag-to-look)
 * with keyboard and mouse into one sample per fixed simulation step.
 */
export class InputController {
  onLook: (dxPx: number, dyPx: number) => void = () => {};
  /** Return true to consume a press for a two-step targeted ability. */
  onPress:(button:ButtonName)=>boolean=()=>false;

  private stickX = 0;
  private stickY = 0;
  private stickPointer: number | null = null;
  private stickOrigin = { x: 0, y: 0 };
  private zoneOffset = { left: 0, top: 0 };
  private lookPointer: number | null = null;
  private lookLast = { x: 0, y: 0 };
  private readonly keys = new Set<string>();
  private readonly pending = new Map<ButtonName, number>();
  private readonly stickZone: HTMLElement;
  private readonly stickBase: HTMLElement;
  private readonly stickKnob: HTMLElement;

  constructor(
    stickZone: HTMLElement,
    stickBase: HTMLElement,
    stickKnob: HTMLElement,
    lookZone: HTMLElement,
    buttons: Iterable<HTMLElement>,
  ) {
    this.stickZone = stickZone;
    this.stickBase = stickBase;
    this.stickKnob = stickKnob;
    this.bindStick();
    this.bindLook(lookZone);
    for (const el of buttons) this.bindButton(el);
    this.bindKeyboard();
    // No long-press menus or iOS pinch gestures over the game.
    document.addEventListener("contextmenu", (e) => e.preventDefault());
    document.addEventListener("gesturestart", (e) => e.preventDefault());
  }

  /** True while the player is actively pushing a direction. */
  get steering(): boolean {
    const { sx, sy } = this.direction();
    return sx !== 0 || sy !== 0;
  }

  press(button: ButtonName): void {
    if(this.onPress(button))return;
    this.pending.set(button, PRESS_BUFFER_STEPS[button]);
  }

  /** The second step of an aimed ability: its press goes out with the next step. */
  confirmTargetedAbility(button:ButtonName='Special1'):void {this.pending.set(button,1);}
  clearTargetedAbility(button:ButtonName='Special1'):void {this.pending.delete(button);}

  /** Called exactly once per fixed step that is sent to the server. */
  sample(): StepSample {
    let buttons = 0;
    for (const [name, steps] of this.pending) {
      buttons |= Button[name];
      if (steps <= 1) this.pending.delete(name);
      else this.pending.set(name, steps - 1);
    }
    return { ...this.direction(), buttons };
  }

  private direction(): { sx: number; sy: number } {
    let sx = this.stickX;
    let sy = this.stickY;
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) sx -= 1;
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) sx += 1;
    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) sy += 1;
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) sy -= 1;
    const len = Math.hypot(sx, sy);
    if (len > 1) {
      sx /= len;
      sy /= len;
    }
    return { sx, sy };
  }

  private bindStick(): void {
    const zone = this.stickZone;
    zone.addEventListener("pointerdown", (e) => {
      if (this.stickPointer !== null) return;
      e.preventDefault();
      this.stickPointer = e.pointerId;
      capture(zone, e.pointerId);
      this.zoneOffset = zone.getBoundingClientRect();
      this.stickOrigin = { x: e.clientX, y: e.clientY };
      this.stickBase.classList.add("active");
      this.placeStickBase();
      this.moveStick(e.clientX, e.clientY);
    });
    zone.addEventListener("pointermove", (e) => {
      if (e.pointerId === this.stickPointer) this.moveStick(e.clientX, e.clientY);
    });
    const release = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      this.stickPointer = null;
      this.stickX = 0;
      this.stickY = 0;
      this.stickKnob.style.transform = "";
      this.stickBase.classList.remove("active");
      this.stickBase.style.transform = "";
    };
    zone.addEventListener("pointerup", release);
    zone.addEventListener("pointercancel", release);
  }

  private moveStick(x: number, y: number): void {
    let dx = x - this.stickOrigin.x;
    let dy = y - this.stickOrigin.y;
    const len = Math.hypot(dx, dy);
    if (len > STICK_RADIUS) {
      // Floating stick: the base follows the thumb instead of saturating forever.
      this.stickOrigin.x += dx * (1 - STICK_RADIUS / len);
      this.stickOrigin.y += dy * (1 - STICK_RADIUS / len);
      this.placeStickBase();
      dx = x - this.stickOrigin.x;
      dy = y - this.stickOrigin.y;
    }
    this.stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
    this.stickX = dx / STICK_RADIUS;
    this.stickY = -dy / STICK_RADIUS;
  }

  /** The base lives inside the zone, so convert from viewport coordinates. */
  private placeStickBase(): void {
    const x = this.stickOrigin.x - this.zoneOffset.left;
    const y = this.stickOrigin.y - this.zoneOffset.top;
    this.stickBase.style.transform = `translate(${x}px, ${y}px)`;
  }

  private bindLook(zone: HTMLElement): void {
    zone.addEventListener("pointerdown", (e) => {
      if (this.lookPointer !== null) return;
      e.preventDefault();
      this.lookPointer = e.pointerId;
      capture(zone, e.pointerId);
      this.lookLast = { x: e.clientX, y: e.clientY };
    });
    zone.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.lookPointer) return;
      this.onLook(e.clientX - this.lookLast.x, e.clientY - this.lookLast.y);
      this.lookLast = { x: e.clientX, y: e.clientY };
    });
    const release = (e: PointerEvent): void => {
      if (e.pointerId === this.lookPointer) this.lookPointer = null;
    };
    zone.addEventListener("pointerup", release);
    zone.addEventListener("pointercancel", release);
  }

  private bindButton(el: HTMLElement): void {
    const name = el.dataset.button as ButtonName | undefined;
    if (!name || !BUTTON_NAMES.includes(name)) return;
    el.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      capture(el, e.pointerId);
      el.classList.add("pressed");
      this.press(name);
    });
    const release = (): void => el.classList.remove("pressed");
    el.addEventListener("pointerup", release);
    el.addEventListener("pointercancel", release);
    el.addEventListener("lostpointercapture", release);
  }

  private bindKeyboard(): void {
    window.addEventListener("keydown", (e) => {
      // Typing in the chat moves nobody.
      if (e.repeat || isTyping(e)) return;
      this.keys.add(e.code);
      const button = KEY_BUTTONS[e.code];
      if (button) this.press(button);
      if (e.code === "KeyQ") this.onLook(-60, 0);
      if (e.code === "KeyE") this.onLook(60, 0);
      if (e.code === "Space" || e.code.startsWith("Arrow")) e.preventDefault();
    });
    window.addEventListener("keyup", (e) => this.keys.delete(e.code));
    window.addEventListener("blur", () => this.keys.clear());
  }
}

/** A key typed into a text field (the chat), not a command. */
export function isTyping(e: KeyboardEvent): boolean {
  return e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
}

/** Pointer capture fails if the pointer is already gone (very quick taps): not fatal. */
function capture(el: HTMLElement, pointerId: number): void {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    /* ignore */
  }
}
